import * as m from "@paraglide/messages"
import PlaydataUnsubscribe from "@playdata/PlaydataUnsubscribe"
import type { SupportedLocale } from "@site-types/content"
import { createFileRoute } from "@tanstack/react-router"
import { playdataRouteHead } from "@utils/playdata-route-head"

/**
 * Where a newsletter's unsubscribe link lands.
 *
 * `noindex`, like `thanks`: it is reachable only from an email, it is
 * per-subscriber, and there is nothing here for a search engine.
 *
 * The route only reads the link's parameters. It deliberately does **not**
 * unsubscribe in a loader — mail scanners fetch every link in an incoming
 * email, and a loader side effect would remove people who never clicked. The
 * component asks first and acts on the button.
 */
export const Route = createFileRoute("/$locale/playdata/newsletter-unsubscribe")({
  validateSearch: (search: Record<string, unknown>) => ({
    id: typeof search.id === "string" ? search.id : "",
    token: typeof search.token === "string" ? search.token : "",
  }),
  head: ({ params }) => {
    const locale = params.locale as SupportedLocale
    return playdataRouteHead({
      description: m.playdata_newsletter_unsubscribe_meta_description({}, { locale }),
      locale,
      path: "/playdata/newsletter-unsubscribe",
      robots: "noindex",
      title: m.playdata_newsletter_unsubscribe_meta_title({}, { locale }),
    })
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { locale: raw } = Route.useParams()
  const { id, token } = Route.useSearch()
  return (
    <PlaydataUnsubscribe
      id={id}
      locale={raw as SupportedLocale}
      token={token}
    />
  )
}
